import { useState, useEffect, useRef } from 'react';

export default function ThreeDSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: '3D Model Development',
      description: 'Creating detailed, accurate 3D models for products, prototypes, and visualizations using advanced modeling software and techniques.',
      icon: 'ri-box-3-line'
    },
    {
      title: 'Multi-Colour 3D Printing',
      description: 'Bringing your designs to life with state-of-the-art multi-color 3D printing technology for vibrant, detailed physical prototypes.',
      icon: 'ri-printer-line'
    },
    {
      title: 'Industrial Design & Prototyping',
      description: 'Developing functional prototypes and industrial designs that bridge the gap between concept and manufacturing-ready products.',
      icon: 'ri-tools-line'
    },
    {
      title: 'Architectural & Product Visualization',
      description: 'Creating stunning photorealistic renderings and animations for architectural projects and product presentations.',
      icon: 'ri-building-line'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="threed"
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('/assets/3d-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-black/80 to-black/60"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Services List */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="space-y-6">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-blue-500/20 hover:border-blue-500/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer"
                  onMouseEnter={() => setHoveredService(index)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center group-hover:animate-pulse flex-shrink-0">
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                      <i className="ri-arrow-right-line text-blue-400 text-xl"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-box-3-line text-2xl text-white"></i>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                Bringing Your Concepts to 
                <span className="bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent"> Vibrant, Tangible Life</span>
              </h2>
            </div>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              Transform your ideas from digital concepts to physical reality with our advanced 3D development 
              and multi-color printing services. Whether you need detailed prototypes, architectural models, 
              or product visualizations, we combine cutting-edge technology with creative expertise to deliver 
              stunning, functional results that exceed expectations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a 
                href="/services/threed"
                className="group bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer no-underline text-center"
              >
                <span className="flex items-center justify-center gap-2">
                  Explore 3D Development & Printing
                  <i className="ri-box-3-line group-hover:animate-spin"></i>
                </span>
              </a>
              
              <button className="group border-2 border-blue-500 hover:bg-blue-500 text-blue-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                <span className="flex items-center justify-center gap-2">
                  Request 3D Quote
                  <i className="ri-calculator-line group-hover:rotate-12 transition-transform duration-300"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}